import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  useToast,
  Box,
  Container,
  Progress,
  VStack,
  Text,
  Skeleton,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import CommunityMirrorView from "./CommunityMirrorView";
import { getCommunityMirrorPipeline } from "../../api";

function CommunityMirrorViewContainer() {
  const { projectId } = useParams();
  const [pipelineData, setPipelineData] =
    useState<CommunityMirrorPipeline | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    const fetchPipelineData = async () => {
      if (!projectId) return;

      try {
        setIsLoading(true);
        setError(null);
        const data = await getCommunityMirrorPipeline(Number(projectId));
        setPipelineData(data);
      } catch (error) {
        setError("Failed to fetch pipeline data");
        toast({
          title: "Error",
          description: "Failed to fetch pipeline data",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPipelineData();
  }, [projectId, toast]);

  if (isLoading) return <CommunityMirrorViewSkeleton />;
  if (error) return <CommunityMirrorViewError />;
  if (!pipelineData) return <CommunityMirrorViewEmpty />;

  return (
    <CommunityMirrorView
      project={pipelineData.project}
      updates={pipelineData.updates}
    />
  );
}

export default CommunityMirrorViewContainer;

function CommunityMirrorViewSkeleton() {
  return (
    <Container maxW="container.lg" py={{ base: 4, md: 8 }}>
      <Box
        bg="gray.50"
        p={{ base: 4, md: 6 }}
        borderRadius="lg"
        boxShadow="sm"
        border="1px solid"
        borderColor="gray.200"
      >
        <VStack align="stretch" spacing={{ base: 4, md: 6 }}>
          <Skeleton height="32px" width={{ base: "150px", md: "200px" }} />
          <Skeleton height="20px" width={{ base: "120px", md: "150px" }} />
          <Skeleton height="24px" width={{ base: "100%", md: "300px" }} />
          <Progress
            value={0}
            size="lg"
            colorScheme="blue"
            hasStripe
            isAnimated
            borderRadius="full"
          />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={{ base: "80px", md: "100px" }} />
          ))}
        </VStack>
      </Box>
    </Container>
  );
}

function CommunityMirrorViewError() {
  return (
    <Container maxW="container.lg" py={{ base: 4, md: 8 }}>
      <Alert
        status="error"
        variant="subtle"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        borderRadius="lg"
        p={{ base: 4, md: 6 }}
      >
        <AlertIcon boxSize="6" mr={0} mb={3} />
        <Text fontWeight="semibold" fontSize={{ base: "md", md: "lg" }} mb={2}>
          Unable to Load Build Pipeline
        </Text>
        <Text fontSize={{ base: "sm", md: "md" }}>
          We encountered an issue while fetching the build pipeline data. Please
          try again later.
        </Text>
      </Alert>
    </Container>
  );
}

function CommunityMirrorViewEmpty() {
  return (
    <Container maxW="container.lg" py={{ base: 4, md: 8 }}>
      <VStack
        spacing={4}
        align="center"
        bg="gray.50"
        p={{ base: 5, md: 8 }}
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Text
          fontSize={{ base: "md", md: "lg" }}
          fontWeight="semibold"
          color="gray.700"
        >
          No Build Pipeline Data Available
        </Text>
        <Text
          color="gray.600"
          textAlign="center"
          maxW="md"
          fontSize={{ base: "sm", md: "md" }}
        >
          There is no build pipeline data available for this project at the
          moment.
        </Text>
      </VStack>
    </Container>
  );
}
